import type { JSX } from 'react';
import { Trans } from 'react-i18next';
import './SecretWordLabel.css';

type SecretWordLabelProps =
  | {
      mode: 'word';
      /** Source string in any case; rendered uppercase via CSS. */
      word: string;
    }
  | {
      mode: 'length';
      /** Letter count; rendered in lining numerals. */
      length: number;
    };

/**
 * The secret-word sentence shown to a player at role assignment.
 *
 * - **Villager / Fool** see the full word: *"The secret word is APPLE."*
 *   The word is italic uppercase, letter-spaced 0.04em, weight 500, ink-deep.
 *   Per the brief: not Cutive Mono. At display scale mono reads as an input
 *   field — wrong affordance for a player who **holds** the word rather than
 *   types it. Mono stays reserved for typed-input surfaces and the round-end
 *   reveal.
 * - **Ghost** sees the length: *"The secret word is 5 letters long."*
 *   The digit is in lining numerals, weight 500, ink-deep.
 *
 * Uses `<Trans>` so the emphasised slot stays a real DOM element (not a
 * string-interpolated HTML payload) — the surrounding italic carries via
 * the parent `<p>`, and the emphasised span carries its own class.
 */
export function SecretWordLabel(props: SecretWordLabelProps): JSX.Element {
  if (props.mode === 'word') {
    return (
      <p className="susdle-secret-word-label">
        <Trans
          i18nKey="meeting.secret.word"
          values={{ word: props.word }}
          components={{ strong: <span className="susdle-secret-word-label__word" /> }}
        />
      </p>
    );
  }
  return (
    <p className="susdle-secret-word-label">
      <Trans
        i18nKey="meeting.secret.length"
        count={props.length}
        components={{ strong: <span className="susdle-secret-word-label__digit" /> }}
      />
    </p>
  );
}
